import { useEffect, useState } from "react";
import { api, fileUrl, apiError, money } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { fabricAvailability, allocate, canUse } from "@/lib/fabricAllocation";
import { ColorFilter, MOCKUP_ACK_TEXT } from "@/lib/colors";
import { toast } from "sonner";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, Plus, Trash2, X, UploadCloud, Loader2, CheckCircle2 } from "lucide-react";

export const MEASUREMENTS = {
  Tops: [["bust", "Bust"], ["underbust", "Underbust"], ["waist", "Waist"], ["torso", "Torso length"]],
  Bottoms: [["waist", "Waist"], ["hips", "Hips"], ["rise", "Rise"]],
  Skirts: [["waist", "Waist"], ["hips", "Hips"], ["length", "Length"]],
  Bodysuits: [["bust", "Bust"], ["underbust", "Underbust"], ["waist", "Waist"], ["hips", "Hips"], ["torso", "Torso length"]],
  "Money Bags": [],
};

const STEPS = ["Pieces", "Fabric", "Add-Ons", "Measurements", "Needed By", "Inspiration", "Review"];

let uidc = 1;

export default function CustomOrder() {
  const { categories, builderCategories } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [styles, setStyles] = useState([]);
  const [addonDefs, setAddonDefs] = useState([]);
  const [pieces, setPieces] = useState(location.state?.pieces || []);
  const [fabricsByStyle, setFabricsByStyle] = useState({});
  const [neededBy, setNeededBy] = useState("");
  const [inspiration, setInspiration] = useState([]);
  const [generalNotes, setGeneralNotes] = useState("");
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [confirm, setConfirm] = useState(false);
  const [mockupAck, setMockupAck] = useState(false);
  const [colorBy, setColorBy] = useState({});
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const catName = (id) => categories.find((c) => c.id === id)?.name || "";

  const priceOfAddon = (key) => Number(addonDefs.find((a) => a.key === key)?.price || 0);
  const estimatePiece = (p) => {
    if (p.base_price === null || p.base_price === undefined || p.base_price === "") return null;
    return Number(p.base_price) + (p.addon_keys || []).reduce((s, k) => s + priceOfAddon(k), 0);
  };
  const anyUnpriced = pieces.some((p) => estimatePiece(p) === null);
  const estimateTotal = pieces.reduce((s, p) => s + (estimatePiece(p) || 0), 0);
  const avail = fabricAvailability(fabricsByStyle);
  const { short } = allocate(pieces, styles, avail);

  useEffect(() => {
    api.get("/styles").then((r) => setStyles(r.data)).catch(() => {});
    api.get("/addons").then((r) => setAddonDefs(r.data)).catch(() => {});
  }, []);

  const addPiece = (s) => {
    setPieces((p) => [...p, {
      uid: uidc++, style_id: s.id, style_name: s.name, category_id: s.category_id,
      category_name: catName(s.category_id), style_image: s.images?.[0] || null,
      base_price: s.base_price ?? null,
      allow_rhinestones: s.allow_rhinestones, allow_ruffles: s.allow_ruffles, allow_hardware: s.allow_hardware,
      fabric_id: null, fabric_name: null, fabric_image: null, addon_keys: [], measurements: {}, notes: "",
    }]);
    toast.success(`${s.name} added`);
  };

  const removePiece = (uid) => setPieces((p) => p.filter((x) => x.uid !== uid));
  const updatePiece = (uid, patch) => setPieces((p) => p.map((x) => (x.uid === uid ? { ...x, ...patch } : x)));

  const loadFabrics = async (styleId, fresh = false) => {
    if (!fresh && fabricsByStyle[styleId]) return;
    try {
      const { data } = await api.get(`/styles/${styleId}/fabrics`);
      setFabricsByStyle((m) => ({ ...m, [styleId]: data }));
    } catch { setFabricsByStyle((m) => ({ ...m, [styleId]: [] })); }
  };

  useEffect(() => {
    if (step === 1) pieces.forEach((p) => loadFabrics(p.style_id, true));
  }, [step]); // eslint-disable-line

  const uploadInspiration = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload/inspiration", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setInspiration((prev) => [...prev, data.url]);
      }
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const canNext = () => {
    if (step === 0) return pieces.length > 0;
    if (step === 1) return short.size === 0;
    return true;
  };

  const submit = async () => {
    if (!confirm) { toast.error("Please confirm your details before submitting"); return; }
    if (!mockupAck) { toast.error("Please check the Custom Design Mockup Acknowledgment before submitting"); return; }
    if (short.size) { toast.error("Not enough fabric available for one of your pieces. Please choose another fabric."); setStep(1); return; }
    setSubmitting(true);
    try {
      const payload = {
        items: pieces.map((p) => ({ style_id: p.style_id, fabric_id: p.fabric_id, addon_keys: p.addon_keys, measurements: p.measurements, notes: p.notes })),
        needed_by: neededBy || null,
        inspiration_images: inspiration,
        general_notes: generalNotes,
        customer_name: customer.name, customer_email: customer.email, customer_phone: customer.phone,
        hp: "",
        mockup_acknowledged: mockupAck,
      };
      const { data } = await api.post("/custom-requests", payload);
      setDone(data.request_number);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(apiError(err));
      // Stock may have changed since the page loaded: refresh so the picker reflects it.
      [...new Set(pieces.map((p) => p.style_id))].forEach((id) => loadFabrics(id, true));
      if (/not enough fabric/i.test(apiError(err))) setStep(1);
    }
    finally { setSubmitting(false); }
  };

  if (done) return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <CheckCircle2 className="mx-auto h-14 w-14 text-kys-accent" />
      <h1 className="mt-6 font-serif text-4xl text-kys-ink">Request submitted!</h1>
      <p className="mt-3 text-sm text-kys-charcoal">Your custom quote request has been received. We'll review it and get back to you — no payment is due until your quote is approved.</p>
      <p data-testid="request-number" className="mt-4 inline-block rounded-md border border-kys-blush-border bg-white px-5 py-2 font-mono text-lg text-kys-ink">{done}</p>
      <p className="mx-auto mt-5 max-w-md text-xs text-kys-muted">
        Want to track it and message us? <Link to="/account/register" data-testid="track-request-link" className="font-semibold text-kys-accent hover:underline">Create an account</Link> or <Link to="/account/login" className="font-semibold text-kys-accent hover:underline">sign in</Link> with the same email.
      </p>
      <div className="mt-8 flex justify-center gap-3">
        <Link to="/" className="rounded-full bg-kys-ink px-6 py-3 text-sm font-semibold text-white hover:bg-kys-accent">Back home</Link>
        <Link to="/shop" className="rounded-full border border-kys-ink px-6 py-3 text-sm font-semibold text-kys-ink hover:bg-kys-ink hover:text-white">Shop ready to ship</Link>
      </div>
    </div>
  );

  const inputCls = "w-full rounded border border-kys-blush-border bg-white px-3 py-2.5 text-sm focus:border-kys-accent focus:outline-none";

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="text-center">
        <p className="label-accent text-kys-accent">Custom Order</p>
        <h1 className="mt-2 font-serif text-4xl tracking-tight text-kys-ink sm:text-5xl">Request a Custom Quote</h1>
        <p className="mx-auto mt-3 max-w-xl text-sm text-kys-charcoal">Build your look from Kys Boutique designs and available fabrics. Submit for review — you won't pay anything until your quote is approved.</p>
      </div>

      {/* Stepper */}
      <div className="no-scrollbar mt-8 flex gap-2 overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => i <= step && setStep(i)} data-testid={`step-${i}`}
            className={`flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition ${i === step ? "bg-kys-ink text-white" : i < step ? "bg-kys-blush-soft text-kys-charcoal" : "bg-white text-kys-muted border border-kys-blush-border"}`}>
            <span className="flex h-5 w-5 items-center justify-center rounded-full border border-current text-[10px]">{i < step ? <Check className="h-3 w-3" /> : i + 1}</span>
            {s}
          </button>
        ))}
      </div>

      <div className="mt-6 min-h-[320px] rounded-lg border border-kys-blush-border bg-white p-5 sm:p-7">
        {/* STEP 0: Pieces */}
        {step === 0 && (
          <div>
            <h2 className="font-serif text-2xl text-kys-ink">Choose Your Pieces</h2>
            <p className="mt-1 text-sm text-kys-muted">Tap a design to add it. Each piece is configured on its own — mix a top, a bottom, a skirt, or just a bodysuit.</p>
            {pieces.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {pieces.map((p) => (
                  <span key={p.uid} className="flex items-center gap-2 rounded-full bg-kys-blush px-3 py-1.5 text-xs font-medium text-kys-ink">
                    {p.style_name} <button onClick={() => removePiece(p.uid)}><X className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
              </div>
            )}
            {builderCategories.map((c) => {
              const list = styles.filter((s) => s.category_id === c.id);
              if (!list.length) return null;
              return (
                <div key={c.id} className="mt-6">
                  <p className="label-accent mb-3 text-kys-muted">{c.name}</p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                    {list.map((s) => (
                      <button key={s.id} data-testid={`add-style-${s.name.toLowerCase()}`} onClick={() => addPiece(s)}
                        className="group overflow-hidden rounded-md border border-kys-blush-border bg-kys-blush text-left transition hover:border-kys-accent">
                        <div className="aspect-[3/4] w-full overflow-hidden bg-kys-blush-soft">
                          {s.images?.[0] ? <img src={fileUrl(s.images[0])} alt={s.name} className="h-full w-full object-cover" /> :
                            <div className="flex h-full w-full items-center justify-center text-xs text-kys-muted">No photo</div>}
                        </div>
                        <div className="flex items-center justify-between p-2">
                          <div>
                            <span className="block text-sm font-medium text-kys-ink">{s.name}</span>
                            <span className="block text-xs text-kys-muted">{s.base_price != null ? `from ${money(s.base_price)}` : "Priced on request"}</span>
                          </div>
                          <Plus className="h-4 w-4 text-kys-accent" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            {styles.length === 0 && <p className="mt-6 text-sm text-kys-muted">No styles available yet. Please check back soon.</p>}
          </div>
        )}

        {/* STEP 1: Fabric */}
        {step === 1 && (
          <div className="space-y-8">
            <h2 className="font-serif text-2xl text-kys-ink">Choose Your Fabric</h2>
            {pieces.map((p) => {
              const allFabrics = fabricsByStyle[p.style_id];
              const color = colorBy[p.uid] || "All";
              const fabrics = allFabrics && (color === "All" ? allFabrics : allFabrics.filter((f) => (f.colors || []).includes(color)));
              return (
                <div key={p.uid} className="rounded-md border border-kys-blush-border p-4">
                  {short.has(p.uid) && (
                    <p data-testid={`fabric-short-${p.uid}`} className="mb-2 rounded bg-red-50 px-3 py-2 text-xs text-red-600">
                      Not enough fabric available for this piece. Please choose another fabric.
                    </p>
                  )}
                  <p className="font-medium text-kys-ink">{p.style_name} <span className="text-xs text-kys-muted">· {p.category_name}</span></p>
                  {fabrics === undefined ? <p className="mt-2 text-sm text-kys-muted">Loading fabrics…</p> :
                    allFabrics.length === 0 ? (
                      <p className="mt-2 rounded bg-kys-blush px-3 py-2 text-xs text-kys-charcoal">No fabrics are currently available online for this design. We'll discuss fabric options with you during review.</p>
                    ) : (
                      <>
                      <ColorFilter value={color} onChange={(c) => setColorBy({ ...colorBy, [p.uid]: c })} testid={`color-filter-${p.uid}`} />
                      {fabrics.length === 0 && <p data-testid={`no-color-match-${p.uid}`} className="mt-3 rounded bg-kys-blush px-3 py-2 text-xs text-kys-charcoal">No {color.toLowerCase()} fabrics for this design. Try another color or All.</p>}
                      <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
                        {fabrics.map((f) => {
                          const img = f.mockup_image || f.fabric_images?.[0];
                          const sel = p.fabric_id === f.fabric_id;
                          const ok = canUse(p, f.fabric_id, pieces, styles, avail, short);
                          return (
                            <button key={f.fabric_id} data-testid={`fabric-${p.uid}-${f.fabric_id}`} disabled={!ok} aria-disabled={!ok}
                              title={ok ? f.fabric_name : "Not enough fabric available for this piece"}
                              onClick={() => ok && updatePiece(p.uid, { fabric_id: f.fabric_id, fabric_name: f.fabric_name, fabric_image: img })}
                              className={`overflow-hidden rounded-md border text-left transition ${!ok ? "cursor-not-allowed" : ""} ${sel ? (ok ? "border-kys-accent ring-2 ring-kys-accent/40" : "border-red-300") : "border-kys-blush-border"} ${ok && !sel ? "hover:border-kys-accent" : ""}`}>
                              <div className={!ok ? "opacity-40" : ""}>
                              <div className="aspect-square w-full bg-kys-blush-soft">
                                {img ? <img src={fileUrl(img)} alt={f.fabric_name} className="h-full w-full object-cover" /> :
                                  <div className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-kys-muted">{f.fabric_name}</div>}
                              </div>
                              <p className="truncate p-1.5 text-[11px] text-kys-charcoal">{f.fabric_name}</p>
                              </div>
                              {!ok && <p data-testid={`fabric-unavailable-${p.uid}-${f.fabric_id}`} className="px-1.5 pb-1.5 text-[10px] leading-tight text-red-600">Not enough fabric available for this piece</p>}
                            </button>
                          );
                        })}
                      </div>
                      </>
                    )}
                </div>
              );
            })}
          </div>
        )}

        {/* STEP 2: Add-ons */}
        {step === 2 && (
          <div className="space-y-6">
            <h2 className="font-serif text-2xl text-kys-ink">Available Add-Ons</h2>
            <p className="text-sm text-kys-muted">Only add-ons available for each design are shown. Pricing for add-ons is confirmed with your quote.</p>
            {pieces.map((p) => {
              const eligible = addonDefs.filter((a) => a.enabled !== false && (a.all_designs || (a.style_ids || []).includes(p.style_id)));
              return (
                <div key={p.uid} className="rounded-md border border-kys-blush-border p-4">
                  <p className="font-medium text-kys-ink">{p.style_name} {p.base_price != null && <span className="ml-1 text-xs font-mono text-kys-muted">base {money(p.base_price)}</span>}</p>
                  {eligible.length === 0 ? <p className="mt-2 text-xs text-kys-muted">No optional add-ons available for this design.</p> : (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {eligible.map((def) => {
                        const key = def.key;
                        const on = p.addon_keys.includes(key);
                        return (
                          <button key={key} data-testid={`addon-${p.uid}-${key}`}
                            onClick={() => updatePiece(p.uid, { addon_keys: on ? p.addon_keys.filter((k) => k !== key) : [...p.addon_keys, key] })}
                            className={`rounded-full border px-4 py-2 text-sm font-medium transition ${on ? "border-kys-accent bg-kys-accent text-white" : "border-kys-blush-border bg-white text-kys-charcoal hover:border-kys-accent"}`}>
                            {def?.name || key}{def?.price ? ` +${money(def.price)}` : ""} {on && <Check className="ml-1 inline h-3.5 w-3.5" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* STEP 3: Measurements */}
        {step === 3 && (
          <div className="space-y-6">
            <h2 className="font-serif text-2xl text-kys-ink">Measurements</h2>
            <p className="text-sm text-kys-muted">Enter measurements (inches) for each piece. Leave blank if unsure — we'll follow up.</p>
            {pieces.map((p) => {
              const fields = MEASUREMENTS[p.category_name] || [];
              return (
                <div key={p.uid} className="rounded-md border border-kys-blush-border p-4">
                  <p className="font-medium text-kys-ink">{p.style_name} <span className="text-xs text-kys-muted">· {p.category_name}</span></p>
                  {fields.length === 0 ? <p className="mt-2 text-xs text-kys-muted">No measurements needed for this piece.</p> : (
                    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {fields.map(([k, label]) => (
                        <div key={k}>
                          <label className="mb-1 block text-xs text-kys-muted">{label}</label>
                          <input data-testid={`meas-${p.uid}-${k}`} value={p.measurements[k] || ""}
                            onChange={(e) => updatePiece(p.uid, { measurements: { ...p.measurements, [k]: e.target.value } })}
                            className={inputCls} placeholder="in" />
                        </div>
                      ))}
                    </div>
                  )}
                  <textarea placeholder="Notes for this piece (fit, color inspo, etc.)" value={p.notes}
                    onChange={(e) => updatePiece(p.uid, { notes: e.target.value })} rows={2} className={`${inputCls} mt-3`} />
                </div>
              );
            })}
          </div>
        )}

        {/* STEP 4: Needed by */}
        {step === 4 && (
          <div>
            <h2 className="font-serif text-2xl text-kys-ink">Needed-By Date</h2>
            <p className="mt-1 text-sm text-kys-muted">When do you need this? Feasibility is confirmed during review.</p>
            <input data-testid="needed-by" type="date" value={neededBy} onChange={(e) => setNeededBy(e.target.value)} className={`${inputCls} mt-4 max-w-xs`} />
          </div>
        )}

        {/* STEP 5: Inspiration */}
        {step === 5 && (
          <div>
            <h2 className="font-serif text-2xl text-kys-ink">Inspiration & Special Requests</h2>
            <div className="mt-3 rounded-md border border-dashed border-kys-gold bg-kys-gold-light/20 p-3 text-xs leading-relaxed text-kys-charcoal">
              Inspiration photos and special requests are welcome! Please note: designs outside currently offered Kys Boutique styles and materials are subject to <strong>approval, material availability, additional pricing, and feasibility</strong>. Uploading a reference photo does not guarantee an exact recreation.
            </div>
            <label data-testid="inspiration-upload" className="mt-4 flex cursor-pointer items-center gap-2 rounded border border-dashed border-kys-blush-border bg-kys-blush px-4 py-3 text-sm font-medium text-kys-charcoal hover:border-kys-accent">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Upload inspiration photos
              <input type="file" accept="image/*" multiple className="hidden" onChange={uploadInspiration} disabled={uploading} />
            </label>
            {inspiration.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-3">
                {inspiration.map((u, i) => (
                  <div key={i} className="relative h-24 w-20 overflow-hidden rounded border border-kys-blush-border">
                    <img src={fileUrl(u)} alt="inspiration" className="h-full w-full object-cover" />
                    <button onClick={() => setInspiration((prev) => prev.filter((_, idx) => idx !== i))} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <textarea data-testid="general-notes" rows={4} placeholder="Anything else you'd like us to know?" value={generalNotes} onChange={(e) => setGeneralNotes(e.target.value)} className={`${inputCls} mt-4`} />
          </div>
        )}

        {/* STEP 6: Review */}
        {step === 6 && (
          <div>
            <h2 className="font-serif text-2xl text-kys-ink">Review Your Request</h2>
            <div className="mt-4 space-y-3">
              {pieces.map((p) => (
                <div key={p.uid} className="flex gap-3 rounded-md border border-kys-blush-border p-3">
                  <div className="h-20 w-16 shrink-0 overflow-hidden rounded bg-kys-blush">
                    {(p.fabric_image || p.style_image) && <img src={fileUrl(p.fabric_image || p.style_image)} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="text-sm">
                    <p className="font-medium text-kys-ink">{p.style_name} <span className="text-xs text-kys-muted">· {p.category_name}</span></p>
                    <p className="text-xs text-kys-charcoal">Starting price: {estimatePiece(p) != null ? money(estimatePiece(p)) : "Priced on request"}</p>
                    <p className="text-xs text-kys-charcoal">Fabric: {p.fabric_name || "To discuss"}</p>
                    <p className="text-xs text-kys-charcoal">Add-ons: {p.addon_keys.length ? p.addon_keys.join(", ") : "None"}</p>
                    {Object.keys(p.measurements).length > 0 && <p className="text-xs text-kys-charcoal">Measurements: {Object.entries(p.measurements).filter(([, v]) => v).map(([k, v]) => `${k} ${v}"`).join(", ") || "—"}</p>}
                    {p.notes && <p className="text-xs text-kys-muted">Note: {p.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-md border border-kys-blush-border bg-kys-blush p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-kys-ink">Estimated starting total</span>
                <span className="font-mono text-lg text-kys-ink">{money(estimateTotal)}{anyUnpriced ? "+" : ""}</span>
              </div>
              <p className="mt-1 text-xs text-kys-muted">Based on design base prices{addonDefs.some((a) => a.price) ? " and any priced add-ons" : ""}. {anyUnpriced ? "Some pieces are priced on request. " : ""}This is an estimate only — your final price is confirmed manually in your quote. No payment is due now.</p>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-kys-muted">Needed by:</span> {neededBy || "Flexible"}</div>
              <div><span className="text-kys-muted">Inspiration photos:</span> {inspiration.length}</div>
            </div>
            {generalNotes && <p className="mt-2 text-sm"><span className="text-kys-muted">Notes:</span> {generalNotes}</p>}

            <div className="mt-6 border-t border-kys-blush-border pt-5">
              <p className="label-accent mb-3 text-kys-muted">Your Contact Info</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <input data-testid="cust-name" required placeholder="Full name *" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className={inputCls} />
                <input data-testid="cust-email" required type="email" placeholder="Email *" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className={inputCls} />
                <input data-testid="cust-phone" placeholder="Phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className={inputCls} />
              </div>
              <label className="mt-4 flex items-start gap-2 text-sm text-kys-charcoal">
                <input data-testid="confirm-check" type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-1 accent-kys-accent" />
                I confirm the information above is correct. I understand this is a quote request and no payment is due until my quote is approved.
              </label>
              <div data-testid="mockup-ack-box" className="mt-4 rounded border border-kys-blush-border bg-kys-blush/50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-kys-ink">Custom Design Mockup Acknowledgment</p>
                <label className="mt-2 flex items-start gap-2 text-sm text-kys-charcoal">
                  <input data-testid="mockup-ack-check" type="checkbox" checked={mockupAck} onChange={(e) => setMockupAck(e.target.checked)} className="mt-1 accent-kys-accent" />
                  <span>{MOCKUP_ACK_TEXT}</span>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Nav buttons */}
      <div className="mt-5 flex items-center justify-between">
        <button data-testid="step-back" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
          className="inline-flex items-center gap-1 rounded-full border border-kys-blush-border px-5 py-2.5 text-sm font-medium text-kys-charcoal disabled:opacity-40">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button data-testid="step-next" onClick={() => canNext() ? setStep((s) => s + 1) : toast.error(step === 1 ? "Not enough fabric available for one of your pieces. Please choose another fabric." : "Add at least one piece to continue")}
            className="inline-flex items-center gap-1 rounded-full bg-kys-ink px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-kys-accent">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button data-testid="submit-quote-btn" onClick={submit} disabled={submitting || !customer.name || !customer.email || !mockupAck}
            className="inline-flex items-center gap-2 rounded-full bg-kys-accent px-7 py-2.5 text-sm font-semibold text-white transition hover:bg-kys-accent-dark disabled:opacity-50">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request a Quote"}
          </button>
        )}
      </div>
    </div>
  );
}
